/**
 * Living drawings, step 1: cut the drawn subject out of a paper photo (pure pixels, no DOM).
 *
 *   photo ─► ink mask (local paper threshold, despeckle) ─► close small gaps (dilate)
 *         ─► fill enclosed areas (flood the paper from the border) ─► connected components:
 *            keep the largest blob + big neighbours (ears, tails drawn apart)
 *         ─► sticker border (dilate) ─► soft alpha edge at output size
 *         ─► colour clean-up: paper white-balanced, pencil-only drawings get a pale "magic" tint,
 *            the white sticker rim around the body
 *         ─► eyes: a pair of small marks in the upper body (kept when they are dots / rings,
 *            covered by cartoon eyes when they are dashes or missing)
 */
import { dilate, despeckle, inkMask, makeImg, makeMask, type Img, type Mask } from '../capture';
import type { CreatureEye, CreatureKind } from './types';

export interface CutOptions {
  /** Long side of the analysis mask (px). */
  analysisSide?: number;
  /** Long side of the cut-out image (px). */
  outSide?: number;
  /** Pale fill for pencil-only drawings (hex), null = keep the paper white. */
  tint?: string | null;
}

export interface CreatureCut {
  width: number;
  height: number;
  /** RGBA, straight alpha. */
  rgba: Uint8ClampedArray<ArrayBuffer>;
  eyes: CreatureEye[];
  /** Eyes were drawn by the child (blink overlay) — otherwise cartoon eyes are added. */
  eyesDrawn: boolean;
  kind: CreatureKind;
  /** Accent colour for sparkles (dominant drawn colour, else the tint). */
  accent: string;
  /** The drawing had real colours (crayons / markers). */
  coloured: boolean;
  /** Silhouette area / paper area (0..1). */
  coverage: number;
}

/* --------------------------------- helpers --------------------------------- */

const clamp = (v: number, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const smooth = (a: number, b: number, x: number) => {
  const t = clamp((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export const rgbToHex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('');

/** Area-average downsample (keeps thin pencil lines visible). */
export function downsample(img: Img, w: number, h: number): Img {
  if (w >= img.width && h >= img.height) return img;
  const out = makeImg(w, h);
  const sx = img.width / w;
  const sy = img.height / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * sy);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * sx);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      let n = 0;
      for (let yy = y0; yy < y1; yy++)
        for (let xx = x0; xx < x1; xx++) {
          const i = (yy * img.width + xx) * 4;
          r += img.data[i]!;
          g += img.data[i + 1]!;
          b += img.data[i + 2]!;
          a += img.data[i + 3]!;
          n++;
        }
      const o = (y * w + x) * 4;
      out.data[o] = r / n;
      out.data[o + 1] = g / n;
      out.data[o + 2] = b / n;
      out.data[o + 3] = a / n;
    }
  }
  return out;
}

export interface MaskComponent {
  /** Pixel indices. */
  px: number[];
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** 4-connected components of a mask (iterative, no recursion). */
export function labelComponents(m: Mask, minArea = 1): MaskComponent[] {
  const { width: w, height: h } = m;
  const seen = new Uint8Array(w * h);
  const out: MaskComponent[] = [];
  const stack: number[] = [];
  for (let s = 0; s < w * h; s++) {
    if (!m.data[s] || seen[s]) continue;
    const c: MaskComponent = { px: [], x0: w, y0: h, x1: -1, y1: -1 };
    seen[s] = 1;
    stack.push(s);
    while (stack.length) {
      const i = stack.pop()!;
      c.px.push(i);
      const x = i % w;
      const y = (i - x) / w;
      if (x < c.x0) c.x0 = x;
      if (x > c.x1) c.x1 = x;
      if (y < c.y0) c.y0 = y;
      if (y > c.y1) c.y1 = y;
      if (x > 0 && m.data[i - 1] && !seen[i - 1]) (seen[i - 1] = 1), stack.push(i - 1);
      if (x < w - 1 && m.data[i + 1] && !seen[i + 1]) (seen[i + 1] = 1), stack.push(i + 1);
      if (y > 0 && m.data[i - w] && !seen[i - w]) (seen[i - w] = 1), stack.push(i - w);
      if (y < h - 1 && m.data[i + w] && !seen[i + w]) (seen[i + w] = 1), stack.push(i + w);
    }
    if (c.px.length >= minArea) out.push(c);
  }
  return out;
}

/** Fill every region not reachable from the image border through non-ink pixels. */
export function fillEnclosed(m: Mask): Mask {
  const { width: w, height: h } = m;
  const outside = new Uint8Array(w * h);
  const stack: number[] = [];
  const seed = (i: number) => {
    if (!m.data[i] && !outside[i]) {
      outside[i] = 1;
      stack.push(i);
    }
  };
  for (let x = 0; x < w; x++) seed(x), seed((h - 1) * w + x);
  for (let y = 0; y < h; y++) seed(y * w), seed(y * w + w - 1);
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % w;
    if (x > 0) seed(i - 1);
    if (x < w - 1) seed(i + 1);
    if (i >= w) seed(i - w);
    if (i < w * (h - 1)) seed(i + w);
  }
  const out = makeMask(w, h);
  for (let i = 0; i < w * h; i++) out.data[i] = outside[i] ? 0 : 1;
  return out;
}

/** Separable box blur of a 0/1 mask into a 0..1 field. */
function blurField(m: Mask, r: number): Float32Array {
  const { width: w, height: h } = m;
  const a = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) a[i] = m.data[i]!;
  if (r <= 0) return a;
  const tmp = new Float32Array(w * h);
  const n = 2 * r + 1;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let s = 0;
      for (let k = -r; k <= r; k++) s += a[y * w + Math.min(w - 1, Math.max(0, x + k))]!;
      tmp[y * w + x] = s / n;
    }
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let s = 0;
      for (let k = -r; k <= r; k++) s += tmp[Math.min(h - 1, Math.max(0, y + k)) * w + x]!;
      a[y * w + x] = s / n;
    }
  return a;
}

function sampleField(f: Float32Array, w: number, h: number, x: number, y: number): number {
  const fx = clamp(x, 0, w - 1.001);
  const fy = clamp(y, 0, h - 1.001);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const ax = fx - x0;
  const ay = fy - y0;
  const i = y0 * w + x0;
  const top = f[i]! * (1 - ax) + f[i + 1]! * ax;
  const bot = f[i + w]! * (1 - ax) + f[i + w + 1]! * ax;
  return top * (1 - ay) + bot * ay;
}

function sampleRgb(img: Img, x: number, y: number): [number, number, number] {
  const w = img.width;
  const fx = clamp(x, 0, w - 1.001);
  const fy = clamp(y, 0, img.height - 1.001);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const ax = fx - x0;
  const ay = fy - y0;
  const i00 = (y0 * w + x0) * 4;
  const i10 = i00 + 4;
  const i01 = i00 + w * 4;
  const i11 = i01 + 4;
  const d = img.data;
  const c = (k: number) => (d[i00 + k]! * (1 - ax) + d[i10 + k]! * ax) * (1 - ay) + (d[i01 + k]! * (1 - ax) + d[i11 + k]! * ax) * ay;
  return [c(0), c(1), c(2)];
}

function saturation(r: number, g: number, b: number): number {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  return mx <= 0 ? 0 : (mx - mn) / mx;
}

/* ----------------------------------- main ----------------------------------- */

export const creatureKind = (aspect: number): CreatureKind => (aspect > 1.3 ? 'walker' : aspect < 0.78 ? 'swayer' : 'hopper');

/**
 * Cut the drawn subject out of a straightened paper photo. Returns null when there is no drawing
 * (blank / black photo).
 */
export function cutOutCreature(src: Img, opts: CutOptions = {}): CreatureCut | null {
  const aSide = opts.analysisSide ?? 300;
  const sA = Math.min(1, aSide / Math.max(src.width, src.height));
  const aw = Math.max(16, Math.round(src.width * sA));
  const ah = Math.max(16, Math.round(src.height * sA));
  const small = downsample(src, aw, ah);
  const long = Math.max(aw, ah);

  // 1. ink, minus a thin frame (crop edges / paper border shadows)
  const ink = despeckle(inkMask(small), 1);
  const frame = Math.max(2, Math.round(long * 0.02));
  for (let y = 0; y < ah; y++) for (let x = 0; x < aw; x++) if (x < frame || y < frame || x >= aw - frame || y >= ah - frame) ink.data[y * aw + x] = 0;
  let inkN = 0;
  for (const v of ink.data) inkN += v;
  if (inkN < aw * ah * 0.0015) return null;

  // 2. close gaps, fill enclosed areas
  const closeR = Math.max(1, Math.round(long * 0.012));
  const filled = fillEnclosed(dilate(ink, closeR));

  // 3. components: the subject = largest blob + sizeable neighbours
  const comps = labelComponents(filled, 4).sort((a, b) => b.px.length - a.px.length);
  // A blob spanning (almost) the whole page is the paper edge / a shadow: skip it.
  const isPage = (c: MaskComponent) => c.x1 - c.x0 > aw * 0.94 && c.y1 - c.y0 > ah * 0.94;
  const main = comps.find((c) => !isPage(c));
  if (!main) return null;
  const mw = main.x1 - main.x0 + 1;
  const mh = main.y1 - main.y0 + 1;
  const ex = { x0: main.x0 - mw * 0.35, x1: main.x1 + mw * 0.35, y0: main.y0 - mh * 0.35, y1: main.y1 + mh * 0.35 };
  const body = makeMask(aw, ah);
  const keep = comps.filter((c) => c === main || (!isPage(c) && c.px.length >= main.px.length * 0.06 && c.x1 >= ex.x0 && c.x0 <= ex.x1 && c.y1 >= ex.y0 && c.y0 <= ex.y1));
  for (const c of keep) for (const i of c.px) body.data[i] = 1;

  // 4. sticker = body + white border
  const borderR = Math.max(1, Math.round(long * 0.014));
  const sticker = dilate(body, borderR);
  let bx0 = aw;
  let by0 = ah;
  let bx1 = -1;
  let by1 = -1;
  let bodyArea = 0;
  for (let y = 0; y < ah; y++)
    for (let x = 0; x < aw; x++) {
      const i = y * aw + x;
      if (body.data[i]) bodyArea++;
      if (!sticker.data[i]) continue;
      if (x < bx0) bx0 = x;
      if (x > bx1) bx1 = x;
      if (y < by0) by0 = y;
      if (y > by1) by1 = y;
    }
  const pad = 2;
  bx0 = Math.max(0, bx0 - pad);
  by0 = Math.max(0, by0 - pad);
  bx1 = Math.min(aw - 1, bx1 + pad);
  by1 = Math.min(ah - 1, by1 + pad);
  const bw = bx1 - bx0 + 1;
  const bh = by1 - by0 + 1;

  // 5. output geometry (output px → analysis px → source px)
  const outSide = opts.outSide ?? 512;
  const srcScale = 1 / sA; // analysis px → source px
  const outScale = Math.min(outSide / Math.max(bw, bh), srcScale * 1.5);
  const ow = Math.max(8, Math.round(bw * outScale));
  const oh = Math.max(8, Math.round(bh * outScale));
  const stickerF = blurField(sticker, 1);
  const bodyF = blurField(body, 1);

  // Paper colour (white balance): mean of light, unsaturated pixels inside the body.
  let pr = 0;
  let pg = 0;
  let pb = 0;
  let pn = 0;
  let colN = 0;
  let hueX = 0;
  let hueY = 0;
  for (let i = 0; i < aw * ah; i++) {
    if (!body.data[i]) continue;
    const r = small.data[i * 4]!;
    const g = small.data[i * 4 + 1]!;
    const b = small.data[i * 4 + 2]!;
    const sat = saturation(r, g, b);
    const lum = (r * 299 + g * 587 + b * 114) / 1000;
    if (!ink.data[i] && sat < 0.2 && lum > 120) {
      pr += r;
      pg += g;
      pb += b;
      pn++;
    }
    if (sat > 0.32 && lum > 40) {
      colN++;
      const mx = Math.max(r, g, b);
      const mn = Math.min(r, g, b);
      let hue = 0;
      if (mx !== mn) hue = mx === r ? ((g - b) / (mx - mn)) % 6 : mx === g ? (b - r) / (mx - mn) + 2 : (r - g) / (mx - mn) + 4;
      hueX += Math.cos((hue * Math.PI) / 3) * sat;
      hueY += Math.sin((hue * Math.PI) / 3) * sat;
    }
  }
  const paper: [number, number, number] = pn > 20 ? [pr / pn, pg / pn, pb / pn] : [245, 242, 235];
  const coloured = colN > bodyArea * 0.05;
  const tintRgb = !coloured && opts.tint ? hexToRgb(opts.tint) : null;
  const base: [number, number, number] = tintRgb ? [tintRgb[0] * 0.62 + 255 * 0.38, tintRgb[1] * 0.62 + 255 * 0.38, tintRgb[2] * 0.62 + 251 * 0.38] : [255, 251, 243];
  const rim: [number, number, number] = [255, 253, 248];

  const out = makeImg(ow, oh);
  for (let y = 0; y < oh; y++)
    for (let x = 0; x < ow; x++) {
      const ax = bx0 + (x + 0.5) / outScale - 0.5;
      const ay = by0 + (y + 0.5) / outScale - 0.5;
      const alpha = smooth(0.3, 0.62, sampleField(stickerF, aw, ah, ax, ay));
      const o = (y * ow + x) * 4;
      if (alpha <= 0) continue;
      const inBody = smooth(0.25, 0.75, sampleField(bodyF, aw, ah, ax, ay));
      const [r0, g0, b0] = sampleRgb(src, (ax + 0.5) * srcScale - 0.5, (ay + 0.5) * srcScale - 0.5);
      // white-balance against the paper
      let r = clamp((r0 / paper[0]) * 255, 0, 255);
      let g = clamp((g0 / paper[1]) * 255, 0, 255);
      let b = clamp((b0 / paper[2]) * 255, 0, 255);
      const lum = (r * 299 + g * 587 + b * 114) / 255000;
      const sat = saturation(r, g, b);
      // paper-ish pixels → clean base fill; ink stays, a little deeper
      const paperish = smooth(0.7, 0.93, lum) * (1 - clamp(sat * 3));
      r = r + (base[0] - r) * paperish;
      g = g + (base[1] - g) * paperish;
      b = b + (base[2] - b) * paperish;
      if (lum < 0.6 && sat < 0.25) {
        const k = 0.82 + lum * 0.2;
        r *= k;
        g *= k * 0.97;
        b *= k * 0.94;
      }
      out.data[o] = r + (rim[0] - r) * (1 - inBody);
      out.data[o + 1] = g + (rim[1] - g) * (1 - inBody);
      out.data[o + 2] = b + (rim[2] - b) * (1 - inBody);
      out.data[o + 3] = alpha * 255;
    }

  // 6. eyes
  const toOut = (px: number, py: number) => ({ x: ((px - bx0 + 0.5) * outScale) / ow, y: ((py - by0 + 0.5) * outScale) / oh });
  const eyes = findEyes(ink, body, main, bodyArea);
  let eyeList: CreatureEye[];
  let eyesDrawn = false;
  if (eyes) {
    eyesDrawn = eyes.drawn;
    eyeList = eyes.spots.map((e) => {
      const p = toOut(e.x, e.y);
      return { x: p.x, y: p.y, r: (e.r * outScale) / ow };
    });
  } else {
    eyeList = placeEyes(body, main).map((e) => {
      const p = toOut(e.x, e.y);
      return { x: p.x, y: p.y, r: (e.r * outScale) / ow };
    });
  }
  if (eyesDrawn)
    for (const e of eyeList) {
      // eyelid colour: the body colour just around the eye
      const c = ringColour(out, e.x * ow, e.y * oh, e.r * ow * 1.5);
      if (c) e.lid = c;
    }

  // accent colour
  let accent = opts.tint ?? '#E9783A';
  if (coloured && (hueX || hueY)) {
    const hue = ((Math.atan2(hueY, hueX) * 180) / Math.PI + 360) % 360;
    accent = hslHex(hue, 0.72, 0.6);
  }
  return {
    width: ow,
    height: oh,
    rgba: out.data,
    eyes: eyeList,
    eyesDrawn,
    kind: creatureKind((main.x1 - main.x0 + 1) / (main.y1 - main.y0 + 1)),
    accent,
    coloured,
    coverage: bodyArea / (aw * ah),
  };
}

function hslHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

function ringColour(img: Img, cx: number, cy: number, r: number): string | null {
  let n = 0;
  let rr = 0;
  let gg = 0;
  let bb = 0;
  for (let k = 0; k < 24; k++) {
    const a = (k / 24) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(a) * r);
    const y = Math.round(cy + Math.sin(a) * r);
    if (x < 0 || y < 0 || x >= img.width || y >= img.height) continue;
    const i = (y * img.width + x) * 4;
    if (img.data[i + 3]! < 200) continue;
    const lum = (img.data[i]! * 299 + img.data[i + 1]! * 587 + img.data[i + 2]! * 114) / 1000;
    if (lum < 90) continue; // skip ink
    rr += img.data[i]!;
    gg += img.data[i + 1]!;
    bb += img.data[i + 2]!;
    n++;
  }
  return n >= 4 ? rgbToHex(rr / n, gg / n, bb / n) : null;
}

interface EyeGuess {
  x: number;
  y: number;
  r: number;
}

/**
 * A pair of small marks in the upper part of the body, side by side. Dots / rings → drawn eyes
 * (`drawn`); dashes / ticks (closed eyes, "– –") → positions for cartoon eyes.
 */
export function findEyes(ink: Mask, body: Mask, main: MaskComponent, bodyArea: number): { spots: EyeGuess[]; drawn: boolean } | null {
  const w = ink.width;
  const bw = main.x1 - main.x0 + 1;
  const bh = main.y1 - main.y0 + 1;
  const inner = (x: number, y: number) => {
    // well inside the body (not part of the outline)
    const r = Math.max(2, Math.round(Math.min(bw, bh) * 0.04));
    for (const [dx, dy] of [
      [r, 0],
      [-r, 0],
      [0, r],
      [0, -r],
    ] as const) {
      const xx = x + dx;
      const yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= ink.height || !body.data[yy * w + xx]) return false;
    }
    return true;
  };
  const marks = labelComponents(ink, 2)
    .filter((c) => {
      const a = c.px.length;
      if (a < bodyArea * 0.0004 || a > bodyArea * 0.03) return false;
      const cw = c.x1 - c.x0 + 1;
      const ch = c.y1 - c.y0 + 1;
      if (cw > bw * 0.3 || ch > bh * 0.3) return false;
      const cx = (c.x0 + c.x1) / 2;
      const cy = (c.y0 + c.y1) / 2;
      if (cy > main.y0 + bh * 0.7 || cy < main.y0 + bh * 0.08) return false;
      return inner(Math.round(c.x0) - 1, Math.round(cy)) && inner(Math.round(c.x1) + 1, Math.round(cy)) && inner(Math.round(cx), Math.round(c.y0) - 1);
    })
    .map((c) => {
      const cw = c.x1 - c.x0 + 1;
      const ch = c.y1 - c.y0 + 1;
      return { x: (c.x0 + c.x1) / 2, y: (c.y0 + c.y1) / 2, w: cw, h: ch, round: Math.max(cw, ch) / Math.max(1, Math.min(cw, ch)) <= 2.2 };
    });
  let best: { a: (typeof marks)[number]; b: (typeof marks)[number]; s: number } | null = null;
  for (let i = 0; i < marks.length; i++)
    for (let j = i + 1; j < marks.length; j++) {
      const a = marks[i]!;
      const b = marks[j]!;
      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      if (dy > bh * 0.12 || dx < bw * 0.1 || dx > bw * 0.62) continue;
      const sa = Math.max(a.w, a.h);
      const sb = Math.max(b.w, b.h);
      if (Math.max(sa, sb) / Math.max(1, Math.min(sa, sb)) > 2.6) continue;
      const mid = (a.x + b.x) / 2;
      const s = dy / bh + (Math.abs(mid - (main.x0 + bw / 2)) / bw) * 0.6 + (Math.abs((a.y + b.y) / 2 - (main.y0 + bh * 0.38)) / bh) * 0.4;
      if (!best || s < best.s) best = { a, b, s };
    }
  if (!best) return null;
  const [l, r] = best.a.x < best.b.x ? [best.a, best.b] : [best.b, best.a];
  const drawn = l.round && r.round;
  const spread = Math.abs(r.x - l.x);
  const minR = Math.min(bw, bh) * 0.06;
  const size = (m: typeof l) => (drawn ? Math.max(m.w, m.h) / 2 : Math.min(spread * 0.36, Math.max(minR, Math.max(m.w, m.h) * 0.6)));
  return { drawn, spots: [l, r].map((m) => ({ x: m.x, y: m.y, r: size(m) })) };
}

/** Cartoon-eye positions when no eyes were drawn: upper-middle of the body, both inside it. */
export function placeEyes(body: Mask, main: MaskComponent): EyeGuess[] {
  const w = body.width;
  const bw = main.x1 - main.x0 + 1;
  const bh = main.y1 - main.y0 + 1;
  const inside = (x: number, y: number, r: number) => {
    for (const [dx, dy] of [
      [0, 0],
      [r, 0],
      [-r, 0],
      [0, r],
      [0, -r],
    ] as const) {
      const xx = Math.round(x + dx);
      const yy = Math.round(y + dy);
      if (xx < 0 || yy < 0 || xx >= w || yy >= body.height || !body.data[yy * w + xx]) return false;
    }
    return true;
  };
  // centre column of the body at the eye line
  let r = Math.max(2, Math.min(bw, bh) * 0.09);
  for (let attempt = 0; attempt < 24; attempt++) {
    const fy = 0.3 + (attempt % 8) * 0.05;
    const y = main.y0 + bh * fy;
    // horizontal extent of the body on this row
    const row = Math.round(y) * w;
    let x0 = -1;
    let x1 = -1;
    for (let x = main.x0; x <= main.x1; x++)
      if (body.data[row + x]) {
        if (x0 < 0) x0 = x;
        x1 = x;
      }
    if (x0 >= 0) {
      const cx = (x0 + x1) / 2;
      const half = Math.min((x1 - x0) * 0.2, r * 2.4);
      if (half > r * 1.1 && inside(cx - half, y, r * 1.1) && inside(cx + half, y, r * 1.1))
        return [
          { x: cx - half, y, r },
          { x: cx + half, y, r },
        ];
    }
    if (attempt % 8 === 7) r *= 0.75;
  }
  const cx = main.x0 + bw / 2;
  const y = main.y0 + bh * 0.38;
  r = Math.max(2, Math.min(bw, bh) * 0.07);
  return [
    { x: cx - r * 1.6, y, r },
    { x: cx + r * 1.6, y, r },
  ];
}
